(() => {
  'use strict';
  const routeElement = document.getElementById('theme-routes');
  if (!routeElement) return;
  const routes = JSON.parse(routeElement.textContent);
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 1001px)');
  const triggers = new WeakMap();
  const observers = new Map();
  const purchaseForms = new WeakSet();
  let busy = false;
  let revision = 0;
  let toastTimer;
  let helpRequest;
  let cartNoteDraft;
  let couponDraft;
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const scrollBehavior = () => reducedMotion.matches ? 'instant' : 'smooth';

  function closeDialog(dialog, restore = true) {
    if (!dialog?.open) return;
    if (dialog.id === 'info-dialog') { helpRequest?.abort(); helpRequest = null; }
    dialog.close();
    document.body.classList.toggle('modal-open', Boolean($('dialog[open]')));
    $('[data-open-menu]')?.setAttribute('aria-expanded', 'false');
    if (restore) {
      const trigger = triggers.get(dialog);
      (trigger?.isConnected && !trigger.closest('dialog:not([open])') ? trigger : $('.header .brand'))?.focus({ preventScroll: true });
    }
  }
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    const parent = trigger?.closest('dialog[open]');
    const returnTarget = parent ? triggers.get(parent) : trigger;
    $$('dialog[open]').forEach(el => closeDialog(el, false));
    triggers.set(dialog, returnTarget);
    dialog.showModal();
    document.body.classList.add('modal-open');
    $('[data-open-menu]')?.setAttribute('aria-expanded', String(dialog.id === 'menu-dialog'));
    $('[data-close]', dialog)?.focus({ preventScroll: true });
  }
  async function showHelp(target, event) {
    const dialog = $('#info-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const faq = target.dataset.help === 'faq';
    const source = faq ? $('[data-faq-loading]') : target.dataset.help === 'contact' ? $('[data-contact-content]') : $$('[data-policy-content]').find(item => new URL(item.dataset.policyUrl, location.origin).pathname === new URL(target.href).pathname);
    if (!source) return;
    event.preventDefault();
    openDialog(dialog, target);
    $('#info-title').textContent = source.dataset.title;
    const content = $('[data-help-content]', dialog);
    content.replaceChildren(source.content.cloneNode(true));
    $('[data-help-fallback]', dialog).href = target.href;
    $('[data-help-fallback]', dialog).parentElement.hidden = target.dataset.help === 'contact';
    $('.info-scroll', dialog).scrollTop = 0;
    if (!faq) return;
    const controller = new AbortController();
    helpRequest = controller;
    try {
      let list = $('#faq .faq-list');
      if (!list) {
        const response = await fetch(target.href.split('#')[0], { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('FAQ unavailable');
        list = $('#faq .faq-list', new DOMParser().parseFromString(await response.text(), 'text/html'));
      }
      if (controller.signal.aborted || !dialog.open) return;
      if (!list) throw new Error('FAQ unavailable');
      const copy = list.cloneNode(true);
      $$('[id], [data-shopify-editor-block]', copy).forEach(el => { el.removeAttribute('id'); el.removeAttribute('data-shopify-editor-block'); });
      $$('details', copy).forEach(el => el.open = false);
      content.replaceChildren(copy);
    } catch {
      if (!controller.signal.aborted && dialog.open) content.replaceChildren($('[data-faq-error]').content.cloneNode(true));
    }
  }
  function errorText(error) {
    return typeof error?.message === 'string' ? error.message : 'Your bag could not be updated. Please check your bag before trying again.';
  }
  async function jsonRequest(url, body) {
    let response;
    try {
      response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    } catch {
      throw new Error('We could not confirm the update. Check your bag before trying again.');
    }
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.status >= 400 || !data) {
      throw new Error(typeof data?.description === 'string' ? data.description : 'Your bag could not be updated. Please check your bag before trying again.');
    }
    return data;
  }
  function paintCart(html) {
    if (typeof html !== 'string') return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countElement = $('[data-cart-item-count]', parsed);
    if (!countElement) return false;
    const count = Number(countElement.dataset.cartItemCount);
    if (!Number.isSafeInteger(count) || count < 0) return false;
    if (count === 0) { cartNoteDraft = undefined; couponDraft = undefined; }
    const wrapper = $('.shopify-section', parsed);
    const content = wrapper ? wrapper.innerHTML : parsed.body.innerHTML;
    $('#cart-drawer-content').innerHTML = content;
    const main = $('[data-main-cart]');
    if (main) {
      main.innerHTML = content;
      $('[data-cart-page-link]', main)?.remove();
      $$('[id^="drawer-quantity-"]', main).forEach(input => {
        const label = $(`label[for="${input.id}"]`, main);
        input.id = input.id.replace('drawer-', 'page-');
        if (label) label.htmlFor = input.id;
      });
      const close = $('[data-close]', main);
      if (close) {
        const link = document.createElement('a');
        link.href = $('.desktop-nav a').href;
        link.className = close.className;
        link.textContent = close.textContent;
        close.replaceWith(link);
      }
    }
    $$('[data-cart-count]').forEach(el => el.textContent = count);
    $$('.bag-button').forEach(el => el.setAttribute('aria-label', `Open bag, ${count} item${count === 1 ? '' : 's'}`));
    if (cartNoteDraft !== undefined) $$('[data-cart-note]').forEach(input => input.value = cartNoteDraft);
    if (couponDraft !== undefined) $$('[data-coupon-input]').forEach(input => input.value = couponDraft);
    return true;
  }
  async function refreshCart() {
    const currentRevision = revision;
    const url = new URL(routes.root, location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Your bag could not be refreshed. Please open the bag page to check it.');
    const html = await response.text();
    if (currentRevision !== revision) return false;
    if (!paintCart(html)) throw new Error('Your bag could not be refreshed. Please open the bag page to check it.');
    return true;
  }
  function setBusy(value) {
    busy = value;
    $$('[data-cart-form]').forEach(form => form.setAttribute('aria-busy', String(value)));
    $$('.product-purchase-form button[type="submit"], .quick-add-form button[type="submit"], .sticky-product-bar button[type="submit"], [data-cart-form] button[type="submit"], [data-coupon-apply], [data-coupon-remove], [data-coupon-clear], [data-cart-delta], [data-product-delta], [data-cart-quantity], [data-variant-choice], .product-quantity input').forEach(control => {
      if (value) {
        control.dataset.wasDisabled = String(control.disabled);
        control.disabled = true;
      } else if ('wasDisabled' in control.dataset) {
        control.disabled = control.dataset.wasDisabled === 'true';
        delete control.dataset.wasDisabled;
      }
    });
    if (!value) $$('[data-product-section]').forEach(section => updateProduct(section));
  }
  function showToast(text) {
    const toast = $('.toast');
    $('[data-toast-text]', toast).textContent = text;
    toast.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('visible'), 4500);
  }
  async function mutateCart(url, body, errorTarget, after) {
    if (busy) return;
    revision += 1;
    $$('[data-form-error], [data-cart-error]').forEach(el => el.textContent = '');
    setBusy(true);
    try {
      if (url === routes.add) {
        // Check the server cart, never a stale browser copy, before adding again.
        const response = await fetch(routes.cart + '.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Your bag could not be checked. Please try again.');
        const current = await response.json();
        if (!Array.isArray(current.items)) throw new Error('Your bag could not be checked. Please try again.');
        for (const item of body.items) {
          const existing = current.items.filter(line => String(line.variant_id || line.id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0);
          if (existing + item.quantity > 99) throw new Error('Your bag can hold up to 99 of each size. Please choose a smaller quantity.');
        }
      }
      const result = await jsonRequest(url, { ...body, sections: ['cart-drawer'], sections_url: location.pathname });
      if (!paintCart(result.sections?.['cart-drawer'])) {
        try { await refreshCart(); }
        catch { location.assign(routes.cart); return; }
      }
      after?.(result);
    } catch (error) {
      // Stock errors may include a partial server-side adjustment. Never retry a mutation.
      try { await refreshCart(); } catch { /* Keep the error and native bag link available. */ }
      const message = errorText(error);
      if (errorTarget?.isConnected) errorTarget.textContent = message;
      $$('[data-cart-error]').forEach(el => el.textContent = message);
      errorTarget?.scrollIntoView({ block: 'nearest', behavior: scrollBehavior() });
    } finally { setBusy(false); }
  }
  function checkout() {
    const form = document.createElement('form');
    form.action = routes.cart;
    form.method = 'post';
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = 'checkout'; input.value = 'Checkout';
    form.append(input);
    const noteValue = cartNoteDraft ?? $('[data-cart-note]')?.value;
    if (noteValue !== undefined) {
      const note = document.createElement('input');
      note.type = 'hidden'; note.name = 'note'; note.value = noteValue;
      form.append(note);
    }
    document.body.append(form);
    HTMLFormElement.prototype.submit.call(form);
  }
  function updateCoupon(target, remove = false) {
    if (busy) return;
    const region = target.closest('[data-coupon]');
    const scope = target.closest('dialog') || $('[data-main-cart]');
    const input = $('[data-coupon-input]', region);
    const clear = target.hasAttribute('data-coupon-clear');
    const code = clear ? '' : remove ? target.dataset.couponRemove : input.value.trim();
    if (!remove && !code) {
      const status = $('[data-coupon-status]', region);
      status.textContent = region.dataset.emptyMessage; status.dataset.error = 'true'; input.focus(); return;
    }
    const existing = $$('[data-applied-code]', region).map(el => el.dataset.appliedCode);
    const codes = clear ? existing : remove ? existing.filter(value => value.toLowerCase() !== code.toLowerCase()) : [...new Set([...existing, code])];
    const endpoint = new URL('cart/update.js', new URL(routes.root, location.origin));
    const submittedDraft = input.value;
    if (!remove) couponDraft = submittedDraft;
    mutateCart(endpoint.pathname, { discount: codes.join(',') }, $('[data-cart-error]', scope), result => {
      const accepted = $$('[data-applied-code]', scope).some(el => el.dataset.appliedCode.toLowerCase() === code.toLowerCase());
      const checkoutEligible = result.discount_codes?.some(entry => entry.code?.toLowerCase() === code.toLowerCase() && entry.applicable === true);
      if (clear || (!remove && (accepted || checkoutEligible) && couponDraft === submittedDraft)) {
        couponDraft = ''; $$('[data-coupon-input]').forEach(el => el.value = '');
      }
      $$('[data-coupon]').forEach(el => {
        const status = $('[data-coupon-status]', el);
        status.textContent = clear ? el.dataset.clearedMessage : remove ? el.dataset.removedMessage : accepted ? el.dataset.appliedMessage : checkoutEligible ? el.dataset.checkoutMessage : el.dataset.invalidMessage;
        status.dataset.error = String(!remove && !accepted && !checkoutEligible);
      });
      $('[data-coupon-input]', scope)?.focus({ preventScroll: true });
    });
  }
  function variantData(section) {
    try { return JSON.parse($('[data-variants]', section).textContent); } catch { return []; }
  }
  function updateProduct(section, changeUrl = false) {
    const selected = $('[data-variant-choice]:checked', section);
    const variant = variantData(section).find(item => String(item.id) === selected?.value);
    if (!variant) return;
    $('[data-unit-price]', section).textContent = variant.price;
    const compare = $('[data-compare-price]', section);
    compare.hidden = !variant.comparePrice; compare.textContent = variant.comparePrice || '';
    $('[data-variant-format]', section).textContent = `${variant.title === 'Default Title' ? '' : variant.title + ' · '}Extrait de parfum`;
    $('[data-sticky-price]', section).textContent = `${variant.price}${variant.title === 'Default Title' ? '' : ' · ' + variant.title}`;
    const quantity = $('[name="quantity"]', section);
    quantity.min = variant.min; quantity.max = Math.min(99, variant.max); quantity.step = variant.step;
    let value = Number(quantity.value);
    if (!Number.isInteger(value)) value = variant.min;
    value = Math.max(variant.min, Math.min(Number(quantity.max), value));
    quantity.value = variant.min + Math.floor((value - variant.min) / variant.step) * variant.step;
    $$('[data-product-add], [data-buy-now]', section).forEach(button => {
      button.disabled = !variant.available || busy;
      if (button.hasAttribute('data-product-add')) $('span', button).textContent = variant.available ? 'Add to bag' : 'Sold out';
    });
    $('[data-product-delta="-1"]', section).disabled = busy || Number(quantity.value) <= variant.min;
    $('[data-product-delta="1"]', section).disabled = busy || Number(quantity.value) + variant.step > Number(quantity.max);
    if (changeUrl) {
      const url = new URL(location.href); url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      if (variant.image) setGalleryImage(section, variant.image);
    }
  }
  function setGalleryImage(section, src) {
    const image = $('.detail-image img', section);
    if (!image) return;
    image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.src = src;
  }
  function initSections() {
    // Cancel Ajax form submissions before Shopify's document-level analytics
    // observes them. The successful cart Ajax request emits the standard event.
    $$('.product-purchase-form, .quick-add-form').forEach(form => {
      if (purchaseForms.has(form)) return;
      form.addEventListener('submit', submitPurchase);
      purchaseForms.add(form);
    });
    for (const [section, observer] of observers) if (!section.isConnected) { observer.disconnect(); observers.delete(section); }
    $$('[data-product-section]').forEach(section => {
      updateProduct(section);
      if (observers.has(section)) return;
      const form = $('.product-purchase-form', section);
      const observer = new IntersectionObserver(entries => {
        const bar = $('.sticky-product-bar', section);
        if (bar) bar.hidden = entries.some(entry => entry.isIntersecting);
      }, { rootMargin: '-100px 0px 0px' });
      observer.observe(form); observers.set(section, observer);
    });
    mountVideo();
  }
  function mountVideo() {
    $$('.hero-video').forEach(video => {
      video.muted = true; video.playsInline = true;
      video.setAttribute('aria-hidden', 'true'); video.tabIndex = -1;
      video.classList.toggle('is-paused', reducedMotion.matches);
      if (reducedMotion.matches) video.pause();
      else video.play().catch(() => video.classList.add('is-paused'));
    });
  }
  function submitPurchase(event) {
    const form = event.target;
    if (!form.matches('.product-purchase-form, .quick-add-form')) return;
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    const data = new FormData(form);
    const id = data.get('id'); const quantity = Number(data.get('quantity'));
    if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) return;
    const buyNow = event.submitter?.hasAttribute('data-buy-now');
    const item = { id, quantity };
    for (const [key, value] of data) {
      if (key === 'selling_plan' && value) item.selling_plan = value;
      const property = key.match(/^properties\[(.+)\]$/);
      if (property && typeof value === 'string') (item.properties ||= {})[property[1]] = value;
    }
    mutateCart(routes.add, { items: [item] }, $('[data-form-error]', form), () => {
      if (buyNow) checkout();
      else showToast('Added to your bag.');
    });
  }
  document.addEventListener('click', event => {
    const target = event.target.closest('button, a');
    if (!target || target.disabled || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (target.hasAttribute('data-close')) return closeDialog(target.closest('dialog'));
    if (target.hasAttribute('data-coupon-apply')) return updateCoupon(target);
    if (target.hasAttribute('data-coupon-remove')) return updateCoupon(target, true);
    if (target.hasAttribute('data-coupon-clear')) return updateCoupon(target, true);
    if (target.hasAttribute('data-help') || target.hasAttribute('data-help-policy')) return showHelp(target, event);
    if (target.hasAttribute('data-open-menu')) return openDialog($('#menu-dialog'), target);
    if (target.hasAttribute('data-open-bag')) {
      event.preventDefault(); $('.toast').classList.remove('visible');
      const trigger = target.closest('.toast') ? $('.bag-button') : target;
      openDialog($('#bag-dialog'), trigger);
      if (!busy) refreshCart().catch(error => { $('[data-cart-error]', $('#bag-dialog')).textContent = errorText(error); });
      return;
    }
    if (target.hasAttribute('data-zoom')) {
      const source = $('img', target); if (!source) return;
      const image = $('#zoom-dialog img'); image.src = source.currentSrc || source.src; image.alt = source.alt;
      return openDialog($('#zoom-dialog'), target);
    }
    if (target.dataset.galleryImage) return setGalleryImage(target.closest('[data-product-section]'), target.dataset.galleryImage);
    if (target.dataset.productDelta) {
      const section = target.closest('[data-product-section]'); const input = $('[name="quantity"]', section);
      input.value = Number(input.value) + Number(target.dataset.productDelta) * Number(input.step);
      updateProduct(section);
      if (target.disabled) $('[data-product-delta]:not(:disabled)', section)?.focus({ preventScroll: true });
      return;
    }
    if (target.dataset.cartDelta || target.hasAttribute('data-cart-remove')) {
      event.preventDefault(); if (busy) return;
      const row = target.closest('[data-line-key]'); const input = $('[data-cart-quantity]', row);
      let quantity = target.hasAttribute('data-cart-remove') ? 0 : Math.min(99, Number(input.max), Math.max(0, Number(input.value) + Number(target.dataset.cartDelta) * Number(input.step)));
      if (quantity < Number(input.dataset.min)) quantity = 0;
      return changeLine(row, quantity, target.dataset.cartDelta);
    }
    if (target.hasAttribute('data-top')) {
      event.preventDefault(); $('#main').focus({ preventScroll: true });
      const url = new URL(location.href); url.hash = ''; history.replaceState({}, '', url);
      window.scrollTo({ top: 0, behavior: scrollBehavior() }); return;
    }
    if (target.tagName === 'A' && target.closest('dialog')) {
      const dialog = target.closest('dialog'); closeDialog(dialog, false);
      const destination = new URL(target.href);
      if (destination.pathname === location.pathname && destination.hash) {
        const section = document.getElementById(destination.hash.slice(1));
        if (section) { section.tabIndex = -1; section.focus({ preventScroll: true }); }
      }
    }
  });
  function changeLine(row, quantity, delta) {
    const key = row.dataset.lineKey;
    const scope = row.closest('dialog') || $('.cart-page');
    mutateCart(routes.change, { id: key, quantity }, $('[data-cart-error]', scope), () => {
      const next = $$('[data-line-key]', scope).find(item => item.dataset.lineKey === key);
      const control = next && $(`[data-cart-delta="${delta || 1}"]:not(:disabled)`, next);
      (control || $('[data-close]', scope) || $('.drawer-heading', scope))?.focus({ preventScroll: true });
    });
  }
  document.addEventListener('change', event => {
    if (event.target.matches('[data-variant-choice]')) updateProduct(event.target.closest('[data-product-section]'), true);
    if (event.target.matches('.product-quantity input')) updateProduct(event.target.closest('[data-product-section]'));
    if (event.target.matches('[data-cart-quantity]') && !busy) {
      const input = event.target; const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > Math.min(99, Number(input.max)) || (quantity && (quantity < Number(input.dataset.min) || (quantity - Number(input.dataset.min)) % Number(input.step)))) {
        input.setCustomValidity('Enter a valid quantity up to ' + Math.min(99, Number(input.max)) + '.'); input.reportValidity();
      } else { input.setCustomValidity(''); changeLine(input.closest('[data-line-key]'), quantity); }
    }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-cart-quantity]')) event.target.setCustomValidity('');
    if (event.target.matches('[data-cart-note]')) {
      cartNoteDraft = event.target.value;
      $$('[data-cart-note]').forEach(input => { if (input !== event.target) input.value = cartNoteDraft; });
    }
    if (event.target.matches('[data-coupon-input]')) {
      couponDraft = event.target.value;
      $$('[data-coupon-input]').forEach(input => { if (input !== event.target) input.value = couponDraft; });
      $$('[data-coupon-status]').forEach(el => { el.textContent = ''; delete el.dataset.error; });
    }
  });
  document.addEventListener('cancel', event => {
    if (event.target.matches('dialog')) { event.preventDefault(); closeDialog(event.target); }
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-coupon-input]')) { event.preventDefault(); updateCoupon(event.target); return; }
    if (event.key !== 'Tab') return;
    const dialog = $('#info-dialog[open]');
    if (!dialog) return;
    const focusable = $$('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex]:not([tabindex="-1"])', dialog).filter(el => el.getClientRects().length && !el.closest('[hidden]'));
    const first = focusable[0], last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  document.addEventListener('click', event => {
    if (event.target.matches('dialog')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(event.target);
    }
  });
  desktop.addEventListener('change', () => { if (desktop.matches && $('#menu-dialog')?.open) { closeDialog($('#menu-dialog'), false); $('.header .brand')?.focus({ preventScroll: true }); } });
  reducedMotion.addEventListener('change', mountVideo);
  document.addEventListener('shopify:section:load', initSections);
  document.addEventListener('shopify:section:unload', () => {
    $$('dialog[open]').forEach(dialog => closeDialog(dialog, false));
    document.body.classList.remove('modal-open');
    setTimeout(initSections, 0);
  });
  const resync = () => { if (!busy && document.visibilityState === 'visible') refreshCart().catch(() => {}); };
  window.addEventListener('pageshow', event => { if (event.persisted) resync(); });
  window.addEventListener('focus', resync);
  document.addEventListener('visibilitychange', resync);
  initSections();
})();
